// A production build names its main bundle by content hash
// (/static/js/main.<hash>.js). A new release changes the name in index.html,
// so a long-open tab can compare the published name with the one it runs.
const mainBundlePathPattern = /\/static\/js\/main\.[0-9a-f]+\.js/i;

export const appVersionCheckIntervalMs = 5 * 60 * 1000;

/** The main bundle path in an index.html document, or null. */
export const getMainBundlePathFromHtml = (html: string): string | null =>
  html.match(mainBundlePathPattern)?.[0] || null;

/**
 * The main bundle path this tab is running, or null in development (the
 * development bundle has no content hash, so there is nothing to compare).
 */
export const getRunningMainBundlePath = (
  doc: Pick<Document, "querySelectorAll"> = document,
): string | null => {
  const scripts = Array.from(
    doc.querySelectorAll<HTMLScriptElement>("script[src]"),
  );

  for (const script of scripts) {
    const path = getMainBundlePathFromHtml(script.getAttribute("src") || "");
    if (path) {
      return path;
    }
  }

  return null;
};

export type FetchPublishedMainBundlePathInput = {
  fetchImpl?: typeof fetch;
  publicUrl?: string;
};

/**
 * Reads the published index.html past the browser cache. Returns null when
 * the check cannot complete (offline, error page, or no hashed bundle), so a
 * failed check never reports a new version.
 */
export const fetchPublishedMainBundlePath = async ({
  fetchImpl = fetch,
  publicUrl = process.env.PUBLIC_URL || "",
}: FetchPublishedMainBundlePathInput = {}): Promise<string | null> => {
  try {
    const response = await fetchImpl(
      `${publicUrl}/index.html?versionCheck=${Date.now()}`,
      { cache: "no-store", credentials: "same-origin" },
    );

    if (!response.ok) {
      return null;
    }

    return getMainBundlePathFromHtml(await response.text());
  } catch {
    return null;
  }
};

export const isNewAppVersionPublished = ({
  publishedMainBundlePath,
  runningMainBundlePath,
}: {
  publishedMainBundlePath: string | null;
  runningMainBundlePath: string | null;
}): boolean =>
  Boolean(
    runningMainBundlePath &&
      publishedMainBundlePath &&
      publishedMainBundlePath !== runningMainBundlePath,
  );
